import { useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getImageUrl } from '../../config/imageConfig'
import {
  Edit,
  Package,
  Image as ImageIcon,
  CheckCircle,
  XCircle,
  Calendar,
  RefreshCw,
  Store,
  Folder,
  Tag as TagIcon
} from 'lucide-react'
import useProductStore from '@/stores/productStore'
import useAppConfigStore from '@/stores/appConfigStore'
import { categoryService, Category } from '@/services/categoryService'
import { merchantService, Merchant } from '@/services/merchantService'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { DetailSection } from '@/components/ui/DetailSection'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const ProductDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const {
    currentProduct,
    loading,
    error,
    fetchProductById,
    clearCurrentProduct
  } = useProductStore()

  const { availableCurrencies, systemConfig, initializeAppConfig } = useAppConfigStore()

  const [categories, setCategories] = useState<Category[]>([])
  const [merchants, setMerchants] = useState<Merchant[]>([])
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingMerchants, setLoadingMerchants] = useState(false)

  // Create a mapping of currency code to symbol (case-insensitive)
  const currencySymbolMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableCurrencies.forEach((currency) => {
      const code = currency.code?.toUpperCase() || ''
      map[code] = currency.symbol || currency.code || ''
      // Also map lowercase version for case-insensitive lookup
      if (code) {
        map[code.toLowerCase()] = currency.symbol || currency.code || ''
      }
    })
    return map
  }, [availableCurrencies])

  // Format price with currency symbol
  const formatPrice = useCallback((price: number, currencyCode?: string) => {
    if (price == null) return 'N/D'
    
    let effectiveCurrencyCode = currencyCode?.toUpperCase() || ''
    
    // If no currency code provided or empty, use system default
    if (!effectiveCurrencyCode || effectiveCurrencyCode.trim() === '') {
      effectiveCurrencyCode = systemConfig?.default_currency || 'MAD' // Fallback to MAD
    }
    
    // Get symbol from map
    const symbol = currencySymbolMap[effectiveCurrencyCode] ||
                   currencySymbolMap[effectiveCurrencyCode.toLowerCase()] ||
                   effectiveCurrencyCode
    
    // Format price with 2 decimal places
    const formattedPrice = typeof price === 'number'
      ? price.toFixed(2)
      : parseFloat(price).toFixed(2)
    
    // If symbol is a single character (like €, $, £) or 2 characters (CA$, US$) place before price, otherwise after
    if (symbol.length === 1 || symbol.length === 2) {
      return `${symbol}${formattedPrice}`
    } else {
      return `${formattedPrice} ${symbol}`
    }
  }, [currencySymbolMap, systemConfig])

  useEffect(() => {
    // Initialize app config if currencies are not loaded
    if (availableCurrencies.length === 0) {
      initializeAppConfig()
    }
    
    if (id) {
      fetchProductById(id)
    }
    loadCategories()
    loadMerchants()
    return () => {
      clearCurrentProduct()
    }
  }, [id])

  const loadCategories = async () => {
    setLoadingCategories(true)
    try {
      const response = await categoryService.getAll({})
      setCategories(response.data || [])
    } catch (err) {
      console.error('Failed to load categories:', err)
    } finally {
      setLoadingCategories(false)
    }
  }

  const loadMerchants = async () => {
    setLoadingMerchants(true)
    try {
      const response = await merchantService.getAll({ limit: 100 })
      setMerchants(response.merchants || [])
    } catch (err) {
      console.error('Failed to load merchants:', err)
    } finally {
      setLoadingMerchants(false)
    }
  }

  // Create mappings for category and merchant names
  const categoryMap = categories.reduce((acc, category) => {
    acc[category.category_id] = category.name
    return acc
  }, {} as Record<string, string>)

  const merchantMap = merchants.reduce((acc, merchant) => {
    acc[merchant.merchant_id] = merchant.business_name
    return acc
  }, {} as Record<string, string>)

  if (loading && !currentProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    )
  }

  if (error || !currentProduct) {
    return (
      <div className="space-y-6">
        <DetailHeader
          title="Produit non trouvé"
          breadcrumbs={[
            { label: 'Produits', href: '/products' },
            { label: 'Détails', href: '#' }
          ]}
          backUrl="/products"
        />
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Produit non trouvé</h3>
              <p className="text-gray-500 mb-4">
                {error || "Le produit que vous recherchez n'existe pas."}
              </p>
              <Button onClick={() => navigate('/products')}>
                Retour à la liste
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const product = currentProduct

  const breadcrumbs = [
    { label: 'Produits', href: '/products' },
    { label: product.name, href: '#' }
  ]

  const actions = [
    {
      label: 'Actualiser',
      icon: <RefreshCw className="h-5 w-5" />,
      onClick: () => fetchProductById(id!),
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-5 w-5" />,
      onClick: () => navigate(`/products/${id}/edit`),
      variant: 'default' as const
    }
  ]

  const status = product.is_available 
    ? { label: 'Disponible', variant: 'success' as const }
    : { label: 'Indisponible', variant: 'destructive' as const }

  return (
    <div className="space-y-6">
      <DetailHeader
        title={product.name}
        subtitle={`ID: ${product.product_id}`}
        breadcrumbs={breadcrumbs}
        backUrl="/products"
        actions={actions}
        status={status}
      />

      {/* Product details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Main info */}
        <div className="lg:col-span-2 space-y-6">
          <DetailSection title="Aperçu">
            {product.images && product.images.length > 0 ? (
              <div className="space-y-4">
                <div className="relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="h-12 w-12 text-gray-300" />
                  <img
                    src={getImageUrl(product.images[0].image_url)}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                {product.images.length > 1 && (
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                    {product.images.slice(1).map((image, index) => (
                      <div
                        key={index}
                        className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center"
                      >
                        <ImageIcon className="h-6 w-6 text-gray-300" />
                        <img
                          src={getImageUrl(image.image_url)}
                          alt={`${product.name} ${index + 2}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Aucune image disponible</p>
              </div>
            )}
          </DetailSection>

          <DetailSection title="Description">
            <p className="text-gray-700 whitespace-pre-line">{product.description}</p>
          </DetailSection>

          <DetailSection title="Tags">
            {product.tags && product.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="bg-primary-50 text-primary-700 border-primary-200">
                    <TagIcon className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">Aucun tag</p>
            )}
          </DetailSection>
        </div>

        {/* Right column - Sidebar */}
        <div className="space-y-6">
          <DetailSection title="Statut et prix">
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-4 text-center">
                <div className="text-sm text-gray-500 mb-1">Prix</div>
                <div className="text-2xl font-bold text-primary-600">
                  {formatPrice(product.price, product.currency_code || product.currency)}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Disponibilité</span>
                {product.is_available ? (
                  <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Disponible
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="h-4 w-4 mr-1" />
                    Indisponible
                  </Badge>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Stock</span>
                <span className="font-medium">{product.stock_quantity} unités</span>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Informations">
            <div className="space-y-4">
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <Folder className="h-4 w-4 mr-2" />
                  Catégorie
                </div>
                <p className="font-medium">
                  {loadingCategories ? (
                    <span className="text-gray-400">Chargement...</span>
                  ) : (
                    categoryMap[product.category_id] || product.category_id || 'Non catégorisé'
                  )}
                </p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <Store className="h-4 w-4 mr-2" />
                  Marchand
                </div>
                <p className="font-medium">
                  {loadingMerchants ? (
                    <span className="text-gray-400">Chargement...</span>
                  ) : (
                    merchantMap[product.merchant_id] || product.merchant_id || 'Non spécifié'
                  )}
                </p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  Créé le
                </div>
                <p className="font-medium">
                  {new Date(product.created_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date de début
                </div>
                <p className="font-medium">
                  {product.start_date
                    ? new Date(product.start_date).toLocaleDateString('fr-FR')
                    : 'Non définie'}
                </p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <Calendar className="h-4 w-4 mr-2" />
                  Date de fin
                </div>
                <p className="font-medium">
                  {product.end_date
                    ? new Date(product.end_date).toLocaleDateString('fr-FR')
                    : 'Non définie'}
                </p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-600 mb-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Mis à jour le
                </div>
                <p className="font-medium">
                  {new Date(product.updated_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </DetailSection>

          <DetailSection title="Actions rapides">
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/products/${id}/edit`)}
              >
                <Edit className="h-5 w-5 mr-2" />
                Modifier le produit
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/offers?productId=${product.product_id}`)}
              >
                <TagIcon className="h-5 w-5 mr-2" />
                Voir les offres
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(`/merchants/${product.merchant_id}`)}
              >
                <Store className="h-5 w-5 mr-2" />
                Voir le marchand
              </Button>
              
              <Button
                variant={product.is_available ? "destructive" : "default"}
                className="w-full justify-start"
                onClick={() => {
                  if (window.confirm(`Êtes-vous sûr de vouloir ${product.is_available ? 'désactiver' : 'activer'} ce produit ?`)) {
                    // TODO: Implement toggle availability
                    console.log('Toggle availability:', product.product_id)
                  }
                }}
              >
                {product.is_available ? (
                  <>
                    <XCircle className="h-5 w-5 mr-2" />
                    Désactiver le produit
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Activer le produit
                  </>
                )}
              </Button>
            </div>
          </DetailSection>
        </div>
      </div>
    </div>
  )
}

export default ProductDetails
